import * as signalRCore from '@microsoft/signalr';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DestroyRef, NgZone } from '@angular/core';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { FiItemUpdatedEventType } from '@fi-shared/fi-signal-r/fi-item-update-event-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export class FiSignalREvent<T> {
    private onEvent$ = new Subject<T>();

    constructor(
        private eventName: string,
        private ngZone: NgZone,
        private onSubscribed: (subscription: Subscription) => void,
    ) {}

    /* for usage only from FiSignalRService */
    attachToSignalREvent(connection: signalRCore.HubConnection) {
        connection.on(this.eventName, (data: T) => {
            const skipConsoleLog =
                this.eventName === 'UserOnlineUpdated' ||
                (this.eventName === 'ItemUpdated' && data['ActionType'] === ActionType.NotificationCategoryChanged);

            if (!skipConsoleLog) {
                if (this.eventName !== 'ItemUpdated') {
                    console.log('onSignalRCoreEvent ' + this.eventName, data);
                } else {
                    console.log(
                        'onSignalRCoreEvent ' + this.eventName,
                        FiItemUpdatedEventType[data['EventType']],
                        ObjectTypes[data['ObjectType']],
                        ActionType[data['ActionType']],
                        data,
                    );
                }
            }
            this.onEvent$.next(data);
        });
    }

    /* for usage from any place */
    // todo deprecated , use subscribeWithDestroyRef
    subscribe(callback: (data: T) => void, takeUntilObs?: Observable<any>): Subscription {
        // we should gather subscriptions because we have to unsubscribe on logout
        const observable: Observable<T> = takeUntilObs ? this.onEvent$.pipe(takeUntil(takeUntilObs)) : this.onEvent$;

        const sub = this.subscribeInternal(observable, callback);
        return sub;
    }

    subscribeWithDestroyRef(callback: (data: T) => void, destroyRef: DestroyRef): Subscription {
        // we should gather subscriptions because we have to unsubscribe on logout
        const sub = this.subscribeInternal(this.onEvent$.pipe(takeUntilDestroyed(destroyRef)), callback);
        return sub;
    }

    private subscribeInternal(observable: Observable<T>, callback: (data: T) => void): Subscription {
        const sub = observable.subscribe((data) => {
            this.ngZone.run(() => callback(data));
        });
        this.onSubscribed(sub);
        return sub;
    }
}
